import { ApplicationSubmission, CompetencyQuestionTemplate, JobPosition } from "../types";

export const INITIAL_JOBS: JobPosition[] = [
  {
    id: "job-1",
    title: "Livestream Producer",
    department: "Operations",
    location: "Harare, Zimbabwe",
    type: "Full-time",
    workMode: "On-site",
    salaryRange: "$25,000 – $35,000 per year",
    description:
      "Run the technical production of funeral and event livestreams end-to-end — camera setup, audio, switching, and on-site troubleshooting — while keeping families' experience calm and dignified.",
    responsibilities: [
      "Set up and operate cameras, mixers, and streaming software on-site",
      "Troubleshoot connectivity and equipment issues in real time",
      "Keep the family's experience calm and dignified throughout the service",
    ],
    qualifications: [
      "2+ years experience with live video production or broadcast",
      "Comfortable operating cameras, mixers, and streaming software under time pressure",
      "Calm, professional manner when working at sensitive/emotional events",
    ],
    benefits: [
      "Company-provided streaming equipment",
      "Mileage and transport allowance for on-site jobs",
      "Training on new gear and workflows",
    ],
    assessmentSteps: {
      candidateInfo: true,
      personality: true,
      competency: true,
      videoPitch: true,
    },
    competencyQuestions: [
      {
        id: "q1",
        question:
          "A stream drops mid-service due to a weak venue connection. What's your immediate priority?",
        type: "single_choice",
        options: [
          "Switch to a backup connection (mobile hotspot/second SIM) and keep recording locally so nothing is lost",
          "Stop the stream and apologise to the family after the service",
          "Restart the laptop and hope it reconnects",
          "Post an update on social media explaining the issue",
        ],
        correctOptionIndex: 0,
        points: 40,
      },
      {
        id: "q2",
        question:
          "Describe how you'd set up audio for a small chapel service with a single lapel mic and ambient hymn singing.",
        type: "open_text",
        points: 60,
      },
    ],
    active: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: "job-2",
    title: "Sales Representative",
    department: "Sales",
    location: "Harare, Zimbabwe",
    type: "Full-time",
    workMode: "Hybrid",
    salaryRange: "$20,000 – $45,000 per year",
    description:
      "Build relationships with funeral homes and families, guide them through Muzukuru's streaming packages, and manage bookings from enquiry through to the day of the service.",
    responsibilities: [
      "Build and maintain relationships with funeral homes and families",
      "Guide clients through Muzukuru's streaming packages",
      "Manage bookings from enquiry through to the day of the service",
    ],
    qualifications: [
      "2+ years in sales or client-facing account management",
      "Comfortable with CRM tools and following up on leads promptly",
      "Empathetic communication style — many clients are grieving",
    ],
    benefits: [
      "Commission on closed bookings",
      "Company phone and data allowance",
      "Clear progression path into account management",
    ],
    assessmentSteps: {
      candidateInfo: true,
      personality: true,
      competency: true,
      videoPitch: true,
    },
    competencyQuestions: [
      {
        id: "q1",
        question:
          "A funeral home contact wants a same-day booking with no clear budget confirmed yet. What do you do first?",
        type: "single_choice",
        options: [
          "Confirm the package and price with them upfront before committing crew and equipment",
          "Assign crew immediately and sort out payment afterwards",
          "Tell them to call back tomorrow",
          "Offer the most expensive package by default",
        ],
        correctOptionIndex: 0,
        points: 50,
      },
      {
        id: "q2",
        question:
          "How would you handle a grieving family who is unhappy about a technical hiccup during their loved one's service?",
        type: "open_text",
        points: 50,
      },
    ],
    active: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: "job-3",
    title: "Video Editor (Memory Books)",
    department: "Other",
    location: "Harare, Zimbabwe / Remote",
    type: "Contract",
    workMode: "Remote",
    salaryRange: "$15 – $25 per finished video",
    description:
      "Edit recorded livestream footage into keepsake memory book videos for families, working to tight turnaround times while handling sensitive content with care.",
    responsibilities: [
      "Edit recorded livestream footage into keepsake memory book videos",
      "Select music and pacing appropriate for emotional, sensitive content",
      "Deliver finished videos within tight, same-week turnaround times",
    ],
    qualifications: [
      "Proficiency with Premiere Pro, DaVinci Resolve, or similar",
      "Strong sense of pacing and music selection for emotional content",
      "Able to work to same-week deadlines",
    ],
    benefits: [
      "Fully remote, flexible working hours",
      "Paid per finished video, no cap on volume",
      "Steady pipeline of work from ongoing bookings",
    ],
    assessmentSteps: {
      candidateInfo: true,
      personality: true,
      competency: true,
      videoPitch: true,
    },
    competencyQuestions: [
      {
        id: "q1",
        question:
          "What's your usual workflow for organising footage before you start cutting a highlights reel?",
        type: "open_text",
        points: 50,
      },
      {
        id: "q2",
        question:
          "Which export setting best balances quality and file size for a memory book video shared via WhatsApp/email?",
        type: "single_choice",
        options: [
          "H.264 MP4, 1080p, moderate bitrate",
          "Uncompressed AVI at 4K",
          "GIF at 480p",
          "ProRes 4444 at original resolution",
        ],
        correctOptionIndex: 0,
        points: 50,
      },
    ],
    active: true,
    createdAt: new Date().toISOString(),
  },
];

export const INITIAL_COMPETENCY_TEMPLATES: CompetencyQuestionTemplate[] = [
  {
    id: "template-operations",
    name: "Operations Default",
    department: "Operations",
    questions: [
      {
        id: "t-ops-q1",
        question:
          "A stream drops mid-service due to a weak venue connection. What's your immediate priority?",
        type: "single_choice",
        options: [
          "Switch to a backup connection (mobile hotspot/second SIM) and keep recording locally so nothing is lost",
          "Stop the stream and apologise to the family after the service",
          "Restart the laptop and hope it reconnects",
          "Post an update on social media explaining the issue",
        ],
        correctOptionIndex: 0,
        points: 40,
      },
      {
        id: "t-ops-q2",
        question:
          "Describe how you'd set up audio for a small chapel service with a single lapel mic and ambient hymn singing.",
        type: "open_text",
        points: 60,
      },
    ],
    createdAt: new Date().toISOString(),
  },
  {
    id: "template-sales",
    name: "Sales Default",
    department: "Sales",
    questions: [
      {
        id: "t-sales-q1",
        question:
          "A funeral home contact wants a same-day booking with no clear budget confirmed yet. What do you do first?",
        type: "single_choice",
        options: [
          "Confirm the package and price with them upfront before committing crew and equipment",
          "Assign crew immediately and sort out payment afterwards",
          "Tell them to call back tomorrow",
          "Offer the most expensive package by default",
        ],
        correctOptionIndex: 0,
        points: 50,
      },
      {
        id: "t-sales-q2",
        question:
          "How would you handle a grieving family who is unhappy about a technical hiccup during their loved one's service?",
        type: "open_text",
        points: 50,
      },
    ],
    createdAt: new Date().toISOString(),
  },
  {
    id: "template-finance",
    name: "Finance Default",
    department: "Finance",
    questions: [
      {
        id: "t-fin-q1",
        question:
          "You notice a discrepancy between an invoice and the recorded payment for a booking. What's your first step?",
        type: "single_choice",
        options: [
          "Reconcile against the booking record and flag the discrepancy before it's processed further",
          "Ignore it if the amount is small",
          "Adjust the invoice to match the payment without investigating",
          "Wait for the client to notice and raise it",
        ],
        correctOptionIndex: 0,
        points: 50,
      },
      {
        id: "t-fin-q2",
        question: "Describe how you'd prioritize reconciling multiple outstanding invoices at month-end.",
        type: "open_text",
        points: 50,
      },
    ],
    createdAt: new Date().toISOString(),
  },
  {
    id: "template-it",
    name: "IT Default",
    department: "IT",
    questions: [
      {
        id: "t-it-q1",
        question: "A staff member reports they can't log in and it's affecting a live booking. What do you do first?",
        type: "single_choice",
        options: [
          "Verify the account/access issue directly with them and prioritize based on the live booking impact",
          "Tell them to try again later",
          "Reset every account as a precaution",
          "Escalate immediately without gathering details",
        ],
        correctOptionIndex: 0,
        points: 50,
      },
      {
        id: "t-it-q2",
        question: "Describe your approach to triaging multiple support requests that come in at the same time.",
        type: "open_text",
        points: 50,
      },
    ],
    createdAt: new Date().toISOString(),
  },
  {
    id: "template-other",
    name: "General Default",
    department: "Other",
    questions: [
      {
        id: "t-other-q1",
        question: "Describe a time you had to prioritize speed of delivery against quality under a tight deadline.",
        type: "open_text",
        points: 50,
      },
      {
        id: "t-other-q2",
        question: "Describe a complex scenario where you resolved a team blocker or failure.",
        type: "open_text",
        points: 50,
      },
    ],
    createdAt: new Date().toISOString(),
  },
];

export const INITIAL_SUBMISSIONS: ApplicationSubmission[] = [
  {
    id: "sub-1",
    jobId: "job-1",
    jobTitle: "Livestream Producer",
    candidateInfo: {
      fullName: "Tafadzwa Chiweshe",
      email: "tafadzwa.chiweshe@example.co.zw",
      phone: "+263 77 234 5678",
      resumeUrl: "",
      linkedInUrl: "https://linkedin.com/in/tafadzwachiweshe",
    },
      personalityResult: {
      code: "ISTJ",
      title: "Logistician",
      description:
        "Practical and fact-minded individuals, whose reliability cannot be doubted.",
      scores: { mind: 20, energy: 30, nature: 80, tactics: 90 },
    },
    competencyScore: 92,
    competencyMaxScore: 100,
    competencyAnswers: [
      { questionId: "q1", selectedOptionIndex: 0, scoreEarned: 40 },
      {
        questionId: "q2",
        openTextAnswer:
          "I'd clip the lapel mic close to the collar, run a limiter to tame sudden hymn volume spikes, and keep a shotgun mic as backup pointed at the pulpit.",
        scoreEarned: 52,
      },
    ],
    videoPitch: {
      durationSeconds: 85,
      recordedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
    },
    overallScore: 92,
    status: "shortlisted",
    submittedAt: new Date(Date.now() - 86400000 * 2).toISOString(),
  },
  {
    id: "sub-2",
    jobId: "job-2",
    jobTitle: "Sales Representative",
    candidateInfo: {
      fullName: "Ruvimbo Mukamuri",
      email: "ruvimbo.mukamuri@example.co.zw",
      phone: "+263 71 987 6543",
      resumeUrl: "",
      linkedInUrl: "https://linkedin.com/in/ruvimbomukamuri",
    },
    personalityResult: {
      code: "ENFP",
      title: "Campaigner",
      description:
        "Enthusiastic, creative and sociable free spirits, who can always find a reason to smile.",
      scores: { mind: 70, energy: 88, nature: 35, tactics: 25 },
    },
    competencyScore: 88,
    competencyMaxScore: 100,
    competencyAnswers: [
      { questionId: "q1", selectedOptionIndex: 0, scoreEarned: 50 },
      {
        questionId: "q2",
        openTextAnswer:
          "I'd apologise sincerely, explain what happened plainly without excuses, and offer a complimentary memory book edit as a gesture of goodwill.",
        scoreEarned: 38,
      },
    ],
    overallScore: 88,
    status: "under_review",
    submittedAt: new Date(Date.now() - 86400000 * 4).toISOString(),
  },
];

export const PERSONALITY_QUESTIONS = [
  {
    id: 1,
    question:
      "You regularly make new friends and feel energized in large social gatherings.",
    trait: "mind", // Low = Introverted, High = Extraverted
  },
  {
    id: 2,
    question:
      "You spend a lot of time exploring unrealistic yet intriguing ideas and future possibilities.",
    trait: "energy", // Low = Observant, High = Intuitive
  },
  {
    id: 3,
    question:
      "In decision-making, you prioritize objective logic over emotional impact on individuals.",
    trait: "nature", // Low = Feeling, High = Thinking
  },
  {
    id: 4,
    question:
      "You prefer keeping your options open rather than following a strict daily schedule.",
    trait: "tactics", // Low = Prospecting, High = Judging
  },
  {
    id: 5,
    question:
      "You tend to stay calm under pressure even in unexpected chaotic situations.",
    trait: "mind",
  },
] as const;

export { PERSONALITY_TYPES } from "../constants";

